/**
 * Drop zone — drag, click-to-pick, and a paste-text panel. The DOM
 * element is the same in all three pipeline states; the runtime state
 * is toggled via `data-state` so CSS can style transitions without
 * the JS swapping markup.
 *
 * Validation rules per spec §26 step 12:
 *   - .pdf  → accept (route to ingestPdf)
 *   - .docx → accept (route to ingestDocx)
 *   - .txt / .md → accept (read as pasted text is; spec-v50 §1)
 *   - .doc  → reject with the user-facing fix-it message
 *   - other → reject with a generic message
 *
 * Multi-document mode (spec-v4.md §8, Step 41 UI hookup, LAUNCH row v4-d):
 *   - The file input carries `multiple` so users can pick more than one
 *     file in a single picker gesture.
 *   - When ≥ 2 files arrive (drag-drop or picker), the bundle callback
 *     `onFiles` fires instead of `onFile`. The pipeline routes those
 *     through `src/ingest/multi.ts` + `runEngineMulti` +
 *     `buildBundleDocxReport`.
 *   - A single `.zip` drop is also treated as a bundle — the pipeline
 *     unpacks it client-side via `fflate`. We surface this through
 *     `onFiles([zip])` so the pipeline owns the zip-vs-multi-file split.
 *   - **Folder mode** (spec-v4 §8 step 1, LAUNCH row v4-o): a second
 *     hidden `<input type="file" webkitdirectory multiple>` plus a
 *     visible "Choose folder…" affordance lets users pick a directory
 *     tree. Folder drag-drop is handled in the `drop` listener via
 *     `DataTransferItem.webkitGetAsEntry()` + the recursive walker
 *     exported from `src/ingest/multi.ts`. In both cases the
 *     enumerated `File[]` is dispatched through the same `onFiles`
 *     channel — pipeline routing is uniform.
 *
 * Accessibility: the dropzone wrapper is a generic <div> (no role,
 * no tabindex) so it never wraps interactive complete-state children
 * (downloads, <details>, chip row) and trips axe's nested-interactive
 * rule. The keyboard hook is the visually-hidden `<input type="file">`
 * the binder injects with `class="sr-only"` and an aria-label — Tab
 * focuses it, Enter/Space opens the native file picker, and the
 * dropzone's `:focus-within` CSS rule paints a visible focus ring
 * around the whole container. The handlers preventDefault() on drag
 * events so the browser doesn't navigate.
 */

import { ROUND_ARTIFACT } from "../report/round-order.js";
import {
  classifyExtension,
  rejectionForFilename,
  TEXT_EXTENSIONS,
  type AcceptedKind,
} from "../ingest/kinds.js";

export type { AcceptedKind };

/** The name pasted text is analyzed under. It is inside `result_hash`. */
export const PASTED_TEXT_NAME = "pasted-text.txt";

/**
 * The most the paste box takes. The analysis runs on the main thread, and a
 * megabyte of text held it for about five seconds when measured; half that is
 * still some 150 pages. A longer document can be dropped as a `.txt` file.
 */
export const MAX_PASTE_BOX_CHARS = 500_000;

/** Every extension one dropped or picked document may carry. */
const DOCUMENT_ACCEPT = [".pdf", ".docx", ...TEXT_EXTENSIONS].join(",");

export type DropResult =
  | { ok: true; file: File; kind: AcceptedKind }
  | { ok: false; reason: string };

export function validateFile(file: File): DropResult {
  const kind = classifyExtension(file.name);
  if (kind) return { ok: true, file, kind };
  return { ok: false, reason: rejectionForFilename(file.name) };
}

/** What the paste box does with its text: a file to analyze, or why not. */
export function pastedTextFile(text: string): DropResult {
  if (text.trim() === "") return { ok: false, reason: "Paste the contract's text first." };
  if (text.length > MAX_PASTE_BOX_CHARS) {
    return {
      ok: false,
      reason: `That is ${text.length.toLocaleString("en-US")} characters; the paste box takes ${MAX_PASTE_BOX_CHARS.toLocaleString("en-US")}. Save it as a .txt file and drop the file instead.`,
    };
  }
  return {
    ok: true,
    file: new File([text], PASTED_TEXT_NAME, { type: "text/plain" }),
    kind: "text",
  };
}

/** True if the filename indicates a zip archive bundle (spec-v4 §8). */
export function isZipFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".zip");
}

/**
 * Extensions that may ride in a **multi-document bundle**: the two document
 * kinds plus the `.csv` privilege-log data member (add-production-qa-pack).
 * Used to filter folder / multi-file drops so a privilege log dropped
 * alongside the produced documents survives to the bundle pipeline (which
 * reconciles Bates numbering and the log). A lone `.csv` is still rejected by
 * {@link validateFile} — the privilege log is only meaningful next to the
 * documents it describes.
 */
export function isBundleMemberName(name: string): boolean {
  return classifyExtension(name) !== null || name.toLowerCase().endsWith(".csv");
}

/** A saved coherence artifact (`*.coherence.json`) — one round of a negotiation. */
export function isRoundArtifactName(name: string): boolean {
  return ROUND_ARTIFACT.test(name);
}

/**
 * A folder's files worth dispatching: its bundle members, or — a folder of
 * nothing but saved rounds — its round artifacts.
 */
function fromFolder(files: File[]): File[] {
  const members = files.filter((f) => isBundleMemberName(f.name));
  return members.length > 0 ? members : files.filter((f) => isRoundArtifactName(f.name));
}

export type DropzoneOptions = {
  /** Called with the validated single file. */
  onFile: (result: DropResult) => void;
  /**
   * Called with the dropped/picked file list when ≥ 2 files arrive
   * **or** when a single `.zip` arrives. The pipeline owns the bundle
   * branch. When omitted, multi-file drops fall back to `onFile` on
   * the first entry (preserves v1/v3 single-doc behavior).
   */
  onFiles?: (files: File[]) => void;
  /**
   * Called when every file that arrives is a saved coherence artifact
   * (`*.coherence.json`): the rounds of a negotiation, for a trend.
   */
  onRounds?: (files: File[]) => void;
  /** Called when a drag-over begins/ends so CSS can pulse the border. */
  onDragState?: (active: boolean) => void;
  /**
   * Optional sibling container that hosts the folder-pick affordance
   * (an element with `data-role="folder-pick"`). When provided,
   * `bindDropzone` listens for clicks on this element separately so
   * the folder-pick `<button>` does not have to live inside the
   * dropzone (which carries `role="button"`) and trigger axe's
   * `nested-interactive` rule. When omitted, the legacy in-dropzone
   * delegation still works for backwards-compatible unit tests.
   */
  folderPickContainer?: HTMLElement | null;
  /**
   * The paste-text panel: a `<textarea data-role="paste-text">`, a
   * `data-role="paste-submit"` button and a `data-role="paste-note"` line. It
   * starts hidden and is opened by a `data-role="paste-open"` control in
   * `folderPickContainer`. Omitted, there is no paste path.
   */
  pastePanel?: HTMLElement | null;
};

export function bindDropzone(dz: HTMLElement, opts: DropzoneOptions): () => void {
  const input = document.createElement("input");
  input.type = "file";
  // Multi-doc accept list adds .zip for the zip-bundle path (spec-v4 §8).
  // The pipeline still distinguishes ≥ 2 files vs a single `.zip` and
  // rejects unsupported extensions upstream via `validateFile` /
  // `planBundle`.
  input.accept = `${DOCUMENT_ACCEPT},.csv,.zip,.json`;
  input.multiple = true;
  // Visually hidden but keyboard-focusable. The dropzone wrapper has
  // no role/tabindex of its own; this input is the page's keyboard
  // entry point into the analysis flow. Tab focuses it (the parent
  // dropzone gets a `:focus-within` ring); Enter/Space opens the
  // native file picker. The aria-label is the accessible name.
  input.className = "sr-only";
  input.setAttribute("aria-label", "Drop a PDF, DOCX or text file — up to four at once.");
  dz.appendChild(input);

  // Secondary input wired to the folder-picker affordance (spec-v4 §8
  // step 1, LAUNCH row v4-o). `webkitdirectory` is non-standard but
  // widely supported (Chromium 7+, Firefox 50+, Safari 14.1+). Browsers
  // that don't recognise the attribute fall through to the multi-file
  // input automatically. Each enumerated File carries `webkitRelativePath`
  // so the bundle pipeline can de-duplicate by basename if needed.
  const inputDir = document.createElement("input");
  inputDir.type = "file";
  inputDir.accept = `${DOCUMENT_ACCEPT},.csv,.json`;
  inputDir.multiple = true;
  // Set both the property and the attribute so the e2e probe selector
  // `#dropzone input[type="file"][webkitdirectory]` matches.
  (inputDir as unknown as { webkitdirectory: boolean }).webkitdirectory = true;
  inputDir.setAttribute("webkitdirectory", "");
  // Out of Tab order — this input is JS-driven only (via the
  // folder-pick button); putting it in the tab order would create a
  // second focusable file-picker on the page with no visible label.
  inputDir.tabIndex = -1;
  inputDir.setAttribute("aria-hidden", "true");
  inputDir.style.display = "none";
  dz.appendChild(inputDir);

  const dispatch = (files: File[]): void => {
    if (files.length === 0) return;
    // Serialize analyses. Nothing here used to check whether one was already
    // running, so a second drop during a slow analysis started a CONCURRENT
    // pipeline; both finished and each replaced the zone with its own
    // "complete" render, so whichever finished last won — not necessarily the
    // file dropped last. The user could end up reading a finished report for a
    // different document than the one they just dropped, with no error shown.
    // The zone carries `data-state="analyzing"` for the whole of every
    // analysis path (single file, bundle, and both comparison flows), and it
    // visibly names the file being worked on, so refusing here leaves the user
    // looking at an accurate in-progress state rather than a wrong result.
    if (dz.getAttribute("data-state") === "analyzing") return;
    if (opts.onRounds && files.every((f) => isRoundArtifactName(f.name))) {
      opts.onRounds(files);
      return;
    }
    const isBundle = files.length >= 2 || (files.length === 1 && isZipFile(files[0]!));
    if (isBundle && opts.onFiles) {
      opts.onFiles(files);
      return;
    }
    opts.onFile(validateFile(files[0]!));
  };

  const onPick = (): void => {
    input.value = "";
    input.click();
  };
  const onPickFolder = (): void => {
    inputDir.value = "";
    inputDir.click();
  };
  const filesFromList = (list: FileList | null | undefined): File[] => {
    if (!list || list.length === 0) return [];
    const out: File[] = [];
    for (let i = 0; i < list.length; i++) out.push(list[i]!);
    return out;
  };
  const onInputChange = (): void => {
    dispatch(filesFromList(input.files));
  };
  const onInputDirChange = (): void => {
    // Folder picker may include arbitrary files (.DS_Store, README,
    // images). Filter to the PDF/DOCX subset before dispatch so the
    // pipeline doesn't have to second-guess the user. The pipeline's
    // `planBundle` would reject the rest anyway, but rejecting here
    // also keeps the file-count cap (50) measured against the
    // accepted set rather than the raw tree.
    dispatch(fromFolder(filesFromList(inputDir.files)));
  };
  const onClick = (e: Event): void => {
    if (e.target === input || e.target === inputDir) return;
    const target = e.target as HTMLElement | null;
    // Folder-pick affordance: an element annotated with
    // `data-role="folder-pick"` opens the directory picker. The match
    // happens before the generic interactive-child guard so the click
    // still routes to `onPickFolder` even though the element is a
    // <button> / <a>.
    if (target?.closest('[data-role="folder-pick"]')) {
      e.stopPropagation();
      onPickFolder();
      return;
    }
    // The dropzone hosts interactive children in the "complete" / "error"
    // states (download anchors, retry button, "why this playbook?"
    // disclosure). Clicks on those must NOT also re-open the file
    // picker — otherwise macOS shows its NSOpenPanel ("Open" button)
    // on top of the download flow and the file never gets saved.
    if (target?.closest("a, button, summary, details, input, label")) return;
    // The results page is text a reader selects and copies: a click in it is
    // never a request for another file.
    if (target?.closest(".results-page, .results-note, textarea")) return;
    onPick();
  };
  const onDragEnter = (e: DragEvent): void => {
    e.preventDefault();
    opts.onDragState?.(true);
  };
  const onDragOver = (e: DragEvent): void => {
    e.preventDefault();
  };
  const onDragLeave = (e: DragEvent): void => {
    e.preventDefault();
    opts.onDragState?.(false);
  };
  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    opts.onDragState?.(false);

    // Folder drag-drop path (spec-v4 §8 step 1). When any dropped
    // DataTransferItem resolves to a directory entry, walk the tree
    // via `webkitGetAsEntry()` and dispatch the enumerated File[]
    // through the same `onFiles` channel. If no directory entry is
    // present we use the cheaper `dataTransfer.files` path below.
    const items = e.dataTransfer?.items;
    const hasItemsApi =
      items &&
      items.length > 0 &&
      typeof (items[0] as DataTransferItem | undefined)?.webkitGetAsEntry === "function";
    if (hasItemsApi) {
      const entries: FileSystemEntry[] = [];
      let anyDir = false;
      for (let i = 0; i < items!.length; i++) {
        const entry = items![i]!.webkitGetAsEntry?.();
        if (!entry) continue;
        if (entry.isDirectory) anyDir = true;
        entries.push(entry);
      }
      if (anyDir && entries.length > 0) {
        void collectFilesFromEntries(entries).then((files) => {
          dispatch(fromFolder(files));
        });
        return;
      }
    }

    dispatch(filesFromList(e.dataTransfer?.files));
  };

  // Folder-pick clicks on the external container (preferred path).
  const onExternalFolderClick = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('[data-role="folder-pick"]')) {
      e.stopPropagation();
      onPickFolder();
      return;
    }
    if (panel && target?.closest('[data-role="paste-open"]')) {
      panel.hidden = !panel.hidden;
      if (!panel.hidden) pasteBox?.focus();
    }
  };

  // Paste-text panel (spec-v50 §1). Pasted text takes the single-file path
  // under a fixed name, so its report verifies against the same text saved as
  // `pasted-text.txt` and run from the command line.
  const panel = opts.pastePanel ?? null;
  const pasteBox = panel?.querySelector<HTMLTextAreaElement>('[data-role="paste-text"]') ?? null;
  const pasteNote = panel?.querySelector<HTMLElement>('[data-role="paste-note"]') ?? null;
  const onPasteClick = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (!panel || !pasteBox || !target?.closest('[data-role="paste-submit"]')) return;
    if (dz.getAttribute("data-state") === "analyzing") return;
    const result = pastedTextFile(pasteBox.value);
    if (!result.ok) {
      // Said beside the box, where the text still is — not as an error state
      // that would replace the page the reader is typing into.
      if (pasteNote) pasteNote.textContent = result.reason;
      return;
    }
    if (pasteNote) pasteNote.textContent = "";
    pasteBox.value = "";
    panel.hidden = true;
    opts.onFile(result);
  };
  panel?.addEventListener("click", onPasteClick);

  dz.addEventListener("click", onClick);
  dz.addEventListener("dragenter", onDragEnter as EventListener);
  dz.addEventListener("dragover", onDragOver as EventListener);
  dz.addEventListener("dragleave", onDragLeave as EventListener);
  dz.addEventListener("drop", onDrop as EventListener);
  input.addEventListener("change", onInputChange);
  inputDir.addEventListener("change", onInputDirChange);
  if (opts.folderPickContainer) {
    opts.folderPickContainer.addEventListener("click", onExternalFolderClick);
  }

  return () => {
    dz.removeEventListener("click", onClick);
    dz.removeEventListener("dragenter", onDragEnter as EventListener);
    dz.removeEventListener("dragover", onDragOver as EventListener);
    dz.removeEventListener("dragleave", onDragLeave as EventListener);
    dz.removeEventListener("drop", onDrop as EventListener);
    input.removeEventListener("change", onInputChange);
    inputDir.removeEventListener("change", onInputDirChange);
    if (opts.folderPickContainer) {
      opts.folderPickContainer.removeEventListener("click", onExternalFolderClick);
    }
    panel?.removeEventListener("click", onPasteClick);
    dz.removeChild(input);
    dz.removeChild(inputDir);
  };
}

/* -------------------------------------------------------------------------- */
/* Folder drag-drop helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Recursively enumerate every `File` reachable from a flat list of
 * `FileSystemEntry`s (the result of `DataTransferItem.webkitGetAsEntry()`).
 * Skips inaccessible entries silently — the drop UX should never throw
 * because one subdirectory wasn't readable.
 *
 * Exported for tests. Mirrors the candidate-shape walker in
 * `src/ingest/multi.ts` but returns `File[]` so the pipeline's
 * `expandBundleInputs` (which already understands `File[]`) handles
 * the rest.
 */
export async function collectFilesFromEntries(
  entries: ReadonlyArray<FileSystemEntry>,
): Promise<File[]> {
  const out: File[] = [];
  for (const entry of entries) {
    await walkEntry(entry, out);
  }
  return out;
}

async function walkEntry(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    try {
      const file = await new Promise<File>((resolve, reject) => {
        fileEntry.file(resolve, reject);
      });
      out.push(file);
    } catch {
      // Inaccessible / permission-denied entries are silently skipped.
    }
    return;
  }
  if (entry.isDirectory) {
    const dir = entry as FileSystemDirectoryEntry;
    const reader = dir.createReader();
    // 🚨 The same silence the file read above gets, for the same reason — and
    // the DIRECTORY is the case this function's own docstring names. Without
    // it `readAllDirectoryEntries` rejects, the rejection propagates out of
    // the whole walk, and `onDrop` (which has no `.catch`) drops the user's
    // entire folder on the floor: no files, no error, nothing happens. One
    // unreadable subdirectory is a routine thing on a shared drive.
    let children: FileSystemEntry[];
    try {
      children = await readAllDirectoryEntries(reader);
    } catch {
      return;
    }
    for (const child of children) await walkEntry(child, out);
  }
}

function readAllDirectoryEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const next = (): void => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(acc);
          return;
        }
        for (const e of batch) acc.push(e);
        next();
      }, reject);
    };
    next();
  });
}
